import { removeSlashFromTheEnd } from "@helpers/Strings";

export const youTubeUrlBase = "https://youtube.com";
export const tikTokUrlBase = "https://tiktok.com";
export const twitterUrlBase = "https://x.com";
export const facebookUrlBase = "https://facebook.com";
export const instagramUrlBase = "https://instagram.com";

export function buildInstagramProfileUrl(handle: string): string {
  const cleaned = (handle || "").trim();
  return cleaned ? `${instagramUrlBase}/${cleaned}` : "";
}

export function buildFacebookPageUrl(pageUrl?: string | null, pageId?: string | null): string {
  const url = (pageUrl || "").trim();
  if (url) return url;
  const id = (pageId || "").trim();
  return id ? `${facebookUrlBase}/${id}` : "";
}

export function buildTikTokProfileUrl(handle: string): string {
  const cleaned = (handle || "").trim();
  return cleaned ? `${tikTokUrlBase}/@${cleaned}` : "";
}

/** True when the URL is only youtube.com (or www) with no channel path, or /@ with no handle. */
function isBaseYoutubeUrlOnly(url: string): boolean {
  let s = (url || "").trim();
  if (!s) {
    return true;
  }
  if (!/^https?:\/\//i.test(s)) {
    s = `https://${s}`;
  }
  try {
    const u = new URL(s);
    const host = u.hostname.replace(/^www\./i, "");
    if (host !== "youtube.com" && host !== "m.youtube.com") {
      return false;
    }
    const path = u.pathname.replace(/\/$/, "") || "";
    if (!path) {
      return true;
    }
    if (path.startsWith("/@")) {
      return path.slice(2).replace(/\/$/, "").length === 0;
    }
    return false;
  } catch {
    return false;
  }
}

export default {
  cleanYouTubeUrl(ytUrl: string | null): string {
    const input = (ytUrl ?? "").trim();
    if (!input) {
      return "";
    }

    // this code supports all types of the YouTube urls https://support.google.com/youtube/answer/6180214?hl=en
    if (input.includes("/channel/")) {
      const rest = input.split("/channel/").pop() ?? "";
      if (!rest.trim()) {
        return "";
      }
      return youTubeUrlBase + "/channel/" + rest;
    }

    if (isBaseYoutubeUrlOnly(input)) {
      return "";
    }

    if (input.includes("/c/")) {
      const rest = input.split("/c/").pop() ?? "";
      if (!rest.trim()) {
        return "";
      }
      return youTubeUrlBase + "/c/" + rest;
    }

    if (input.includes("/user/")) {
      const rest = input.split("/user/").pop() ?? "";
      if (!rest.trim()) {
        return "";
      }
      return youTubeUrlBase + "/user/" + rest;
    }

    return input;
  },

  cleanTikTokHandle(tikTokHandle: string): string {
    tikTokHandle = removeSlashFromTheEnd((tikTokHandle || "").trim());
    tikTokHandle = tikTokHandle.split("/").pop();
    // removes the "@" and "/" symbols
    tikTokHandle = tikTokHandle.replace(/[@\/]/gim, "");

    return tikTokHandle;
  },

  cleanTwitterHandle(twitterHandle: string): string {
    twitterHandle = removeSlashFromTheEnd((twitterHandle || "").trim());
    twitterHandle = twitterHandle.split("/").pop();
    // removes the "@" and "/" symbols
    twitterHandle = twitterHandle.replace(/[@\/]/gim, "");

    return twitterHandle;
  },

  cleanFacebookUrl(fbUrl: string | null): string {
    let fbUserName = removeSlashFromTheEnd((fbUrl ?? "").trim());
    if (!fbUserName) {
      return "";
    }

    fbUserName = fbUserName.split("/").pop() ?? "";
    // removes all the "@" and "/" symbols
    fbUserName = fbUserName.replace(/[@\/]/gim, "");
    // removes the "profile.php?id=" part if exists
    fbUserName = fbUserName.replace(/^.+id=/gim, "");

    const hostOnly = /^(www\.|m\.)?(facebook|fb)\.com$/i;
    if (!fbUserName || hostOnly.test(fbUserName)) {
      return "";
    }

    return facebookUrlBase + "/" + fbUserName;
  },

  cleanInstagramHandle(instaHandle: string): string {
    instaHandle = removeSlashFromTheEnd((instaHandle || "").trim());
    // removes everything that goes before the username
    instaHandle = instaHandle.replace(/^.+instagram\.com\//gim, "");
    // removes everything that goes after the username
    instaHandle = instaHandle.replace(/\/[^\/]*$/gim, "");

    return instaHandle;
  },
};
